import { createContext, useContext, useState, useEffect } from 'react';

const LanguageContext = createContext();

export const useLanguage = () => useContext(LanguageContext);

export const LanguageProvider = ({ children }) => {
  const [lang, setLang] = useState(() => {
    return localStorage.getItem('ss_lang') || 'en';
  });

  useEffect(() => {
    localStorage.setItem('ss_lang', lang);
    document.documentElement.lang = lang;
  }, [lang]);

  const t = (key) => {
    const val = translations[lang]?.[key];
    if (val !== undefined) return val;
    return translations['en']?.[key] || key;
  };

  const toggle = () => setLang(l => l === 'en' ? 'es' : 'en');

  return (
    <LanguageContext.Provider value={{ lang, setLang, toggle, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

const translations = {
  en: {
    // Nav
    'nav.home': 'Home',
    'nav.about': 'About',
    'nav.memberships': 'Memberships',
    'nav.services': 'Services',
    'nav.resources': 'Resources',
    'nav.contact': 'Contact',
    'nav.join': 'Join EverWash',
    'nav.open': 'Open Daily: 9 AM - 7 PM',
    'nav.tagline': "Tahlequah's Original Tunnel Car Wash",

    // Hero
    'hero.badge': 'Open Now',
    'hero.hours': '9 AM - 7 PM',
    'hero.line1': "Tahlequah's",
    'hero.line2': 'Only Full-Service',
    'hero.line3': 'Car Wash',
    'hero.sub': 'Unlimited washes starting at just',
    'hero.sub2': '. The',
    'hero.sub3': 'original tunnel car wash',
    'hero.sub4': 'in town - hand wash first, then through the tunnel.',
    'hero.cta1': 'Join EverWash',
    'hero.cta2': 'Get Directions',
    'hero.stat1.value': 'Only',
    'hero.stat1.label': 'Full-Service in Town',
    'hero.stat2.value': '7 Days',
    'hero.stat2.label': 'A Week',
    'hero.stat3.label': 'Unlimited Washes',

    // Services preview
    'services.badge': 'Our Services',
    'services.title': 'How We Get Your Car Clean',
    'services.subtitle': 'A two-step process that combines personal attention with automated precision.',
    'services.full.title': 'Full-Service Wash',
    'services.full.desc': 'Hand wash by our trained team, then through the automated tunnel.',
    'services.self.title': 'Self-Service Bays',
    'services.self.desc': 'DIY with professional-grade equipment at your pace.',
    'services.self.feat1': 'High-pressure wand',
    'services.self.feat2': 'Foam brush',
    'services.self.feat3': 'Quarter vacuums ($0.25)',
    'services.cta': 'View All Services',

    // Membership
    'membership.badge': 'Unlimited Washes',
    'membership.title': 'Go Unlimited with EverWash',
    'membership.subtitle': 'Become a member and wash as often as you want for one low monthly price. No contracts, cancel anytime.',
    'membership.feat1.title': 'Unlimited Washes',
    'membership.feat1.desc': 'Wash your car as many times as you need, every single month.',
    'membership.feat2.title': 'No Contracts',
    'membership.feat2.desc': 'Month-to-month membership. Cancel anytime, no questions asked.',
    'membership.feat3.title': 'Premium Quality',
    'membership.feat3.desc': "Sonny's equipment with Ryko SoftGloss for a perfect clean every time.",
    'membership.starting': 'Starting at just',
    'membership.permonth': '/month',
    'membership.desc': "That's less than two single washes! Join thousands of happy members who save money and keep their cars sparkling clean.",
    'membership.cta': 'Start Your Membership',

    // Math
    'math.badge': 'The Math',
    'math.title': 'It Just Makes Sense',
    'math.single': 'Single Wash',
    'math.pervisit': 'Per visit',
    'math.best': 'Best Value',
    'math.everwash': 'EverWash Membership',
    'math.unlimited': 'Unlimited washes/month',
    'math.save': 'You Save',
    'math.saveamt': 'Per month (4+ washes)',
    'math.desc1': 'Wash just',
    'math.desc2': 'twice a month',
    'math.desc3': "and you're already saving. Most members wash",
    'math.desc4': '4-6 times per month',
    'math.desc5': "- that's over",
    'math.desc6': '$40 in savings',
    'math.desc7': 'every single month.',
    'math.cta': 'Start Saving Today',

    // About preview
    'about.badge': 'Since Day One',
    'about.title': 'The Original. The Best.',
    'about.desc': "Super Spray Car Wash II has been keeping Tahlequah's vehicles spotless since day one. We're the original tunnel car wash in town and still the only full-service option around.",
    'about.cta': 'Learn Our Story',

    // Why Choose Us
    'why.badge': 'Why Us',
    'why.title': 'Why Choose Super Spray',

    // Reviews
    'reviews.badge': 'Real Reviews',
    'reviews.title': 'What Our Customers Say',
    'reviews.google': '5.0 on Google Reviews',
    'reviews.cta': 'Leave Us a Review on Google',

    // FAQ
    'faq.badge': 'Common Questions',
    'faq.title': 'Frequently Asked Questions',
    'faq.q1': 'How does your full-service wash work?',
    'faq.a1': "Our full-service wash is a two-step process. First, our trained staff hand washes and preps your vehicle, paying special attention to tough spots. Then, we send you through our automated tunnel for a perfect finish. It's the best of both worlds!",
    'faq.q2': 'What is EverWash and how does it work?',
    'faq.a2': "EverWash is our unlimited wash membership program. For one low monthly price starting at $20, you can wash your car as often as you want. Just download the EverWash app, sign up, and drive through whenever you need a wash. No contracts - cancel anytime.",
    'faq.q3': 'What are your hours and where are you located?',
    'faq.a3': "We're open every day from 9 AM to 7 PM. You'll find us at 1581 S Muskogee Ave, Tahlequah, OK 74464. Easy to get to with plenty of space for all vehicles.",
    'faq.q4': 'Do you offer self-service options?',
    'faq.a4': "Yes! In addition to our full-service wash and automated tunnel, we have self-service bays with professional-grade equipment. Great for trucks, boats, or when you want to wash your way. We also have quarter vacuums ($0.25) that really suck.",
    'faq.q5': 'What kind of equipment do you use?',
    'faq.a5': "We use Sonny's vehicle washing systems, the industry leader, with Ryko SoftGloss and FoamBrite technology. This ensures a thorough, safe clean that protects your vehicle's finish every time.",
    'faq.q6': 'How much can I save with a membership?',
    'faq.a6': "A single wash costs $10-16. With our EverWash membership starting at $20/month for unlimited washes, you save money after just two visits. Most members wash weekly and save over $40 per month.",

    // Location
    'location.badge': 'Visit Us',
    'location.title': 'Come See Us',
    'location.hours': 'Hours',
    'location.hoursval': 'Open Daily: 9 AM - 7 PM',
    'location.address': 'Address',
    'location.phone': 'Phone',
    'location.directions': 'Get Directions',

    // Footer
    'footer.quicklinks': 'Quick Links',
    'footer.contact': 'Contact',
    'footer.hours': 'Hours',
    'footer.rights': 'All rights reserved.',
    'footer.serving': 'Serving Tahlequah, Fort Gibson, Muskogee, Wagoner, Park Hill, Keys, Hubert & surrounding areas',

    // About Page
    'aboutpage.badge': 'Our Story',
    'aboutpage.title': "Tahlequah's Original Tunnel Car Wash",
    'aboutpage.subtitle': 'The only full-service car wash in town, combining personal hand washing with automated tunnel technology.',
    'aboutpage.story.badge': 'How It Started',
    'aboutpage.story.title': 'Built on Quality',
    'aboutpage.story.p1': "Super Spray Car Wash II didn't start as just another car wash. It started with a simple belief: Tahlequah deserved better.",
    'aboutpage.story.p2': "We introduced something new - a full-service experience where every vehicle gets hand washed by our team before going through the automated tunnel. It's the attention to detail that sets us apart.",
    'aboutpage.values.badge': 'What Drives Us',
    'aboutpage.values.title': 'Our Values',
    'aboutpage.community.badge': 'Serving Our Community',
    'aboutpage.community.title': 'Proud to Serve Tahlequah',
    'aboutpage.community.desc': "We're more than a car wash - we're part of the Tahlequah community. From local events to everyday interactions, we're proud to serve our neighbors.",
    'aboutpage.community.area': 'Serving the Area',
    'aboutpage.community.areadesc': "While we're based in Tahlequah, we serve customers from all over the region - Fort Gibson, Muskogee, Wagoner, Park Hill, Keys, Hubert, and beyond.",
    'aboutpage.community.welcome': "Whether you're a local or just passing through, you'll get the same great service and sparkling clean results.",
    'aboutpage.cta.title': 'Experience the Difference',
    'aboutpage.cta.desc': "Come see why we're Tahlequah's favorite car wash. Your first visit will show you what makes us special.",
    'aboutpage.cta.btn': 'Get Your First Wash',

    // Memberships Page
    'membpage.badge': 'Unlimited Washes',
    'membpage.title': 'EverWash Memberships',
    'membpage.subtitle': 'Wash your car as many times as you want for one low monthly price. Starting at just $20/month.',
    'membpage.how.badge': 'Simple & Easy',
    'membpage.how.title': 'How EverWash Works',
    'membpage.step1': 'Download & Sign Up',
    'membpage.step1d': 'Get the EverWash app and choose your membership plan. Registration takes just minutes.',
    'membpage.step2': 'Drive Up & Scan',
    'membpage.step2d': 'When you arrive, open the app and scan at the pay station. Your membership is automatically recognized.',
    'membpage.step3': 'Wash Unlimited',
    'membpage.step3d': "Drive through as many times as you want. There's no daily limit - wash whenever you need.",
    'membpage.plans.badge': 'Membership Plans',
    'membpage.plans.title': 'Choose Your Plan',
    'membpage.plans.sub': 'All plans include unlimited washes with no contracts. Cancel anytime.',
    'membpage.popular': 'Most Popular',
    'membpage.single': 'Single wash:',
    'membpage.getstarted': 'Get Started',
    'membpage.note': '* Prices may vary. View current pricing on EverWash app.',
    'membpage.math.badge': 'The Math',
    'membpage.math.title': 'It Just Makes Sense',

    // Services Page
    'servpage.badge': 'What We Offer',
    'servpage.title': 'Our Services',
    'servpage.subtitle': 'From full-service hand wash to self-service bays, we have everything you need.',
    'servpage.cta': 'Ready for a Clean Car?',
    'servpage.ctadesc': 'Stop by anytime during business hours. No appointment needed!',
    'servpage.directions': 'Get Directions',
    'servpage.membership': 'View Memberships',

    // Contact Page
    'contactpage.badge': 'Get In Touch',
    'contactpage.title': 'Contact Us',
    'contactpage.subtitle': "Have a question? We'd love to hear from you. Send us a message and we'll respond as soon as possible.",
    'contactpage.form.name': 'Your Name',
    'contactpage.form.email': 'Email Address',
    'contactpage.form.phone': 'Phone Number (Optional)',
    'contactpage.form.message': 'Your Message',
    'contactpage.form.send': 'Send Message',
    'contactpage.form.sending': 'Sending...',
    'contactpage.success.title': 'Message Sent!',
    'contactpage.success.desc': "Thank you for reaching out. We'll get back to you as soon as possible.",
    'contactpage.success.call': 'Need immediate help? Call us at',
    'contactpage.info.badge': 'Info',
    'contactpage.info.title': 'Visit Us',
    'contactpage.areas.badge': 'Service Area',
    'contactpage.areas.title': 'Proudly Serving',

    // Resources Page
    'respage.badge': 'Learn & Explore',
    'respage.title': 'Resources',
    'respage.subtitle': 'Tips, guides, and news about car care and keeping your vehicle looking its best.',
    'respage.search': 'Search resources...',
    'respage.noresults': 'No resources found matching your search.',
    'respage.readmore': 'Read More',
  },

  es: {
    // Nav
    'nav.home': 'Inicio',
    'nav.about': 'Nosotros',
    'nav.memberships': 'Membresias',
    'nav.services': 'Servicios',
    'nav.resources': 'Recursos',
    'nav.contact': 'Contacto',
    'nav.join': 'Unirse a EverWash',
    'nav.open': 'Abierto: 9 AM - 7 PM',
    'nav.tagline': 'El Tunel de Lavado Original de Tahlequah',

    // Hero
    'hero.badge': 'Abierto Ahora',
    'hero.hours': '9 AM - 7 PM',
    'hero.line1': 'El Unico',
    'hero.line2': 'Servicio Completo',
    'hero.line3': 'de Lavado',
    'hero.sub': 'Lavados ilimitados desde solo',
    'hero.sub2': '. El',
    'hero.sub3': 'tunel de lavado original',
    'hero.sub4': 'de la ciudad - lavado a mano primero, luego por el tunel.',
    'hero.cta1': 'Unirse a EverWash',
    'hero.cta2': 'Como Llegar',
    'hero.stat1.value': 'Unico',
    'hero.stat1.label': 'Servicio Completo',
    'hero.stat2.value': '7 Dias',
    'hero.stat2.label': 'A la Semana',
    'hero.stat3.label': 'Lavados Ilimitados',

    // Services preview
    'services.badge': 'Nuestros Servicios',
    'services.title': 'Como Limpiamos Tu Auto',
    'services.subtitle': 'Un proceso de dos pasos que combina atencion personal con precision automatizada.',
    'services.full.title': 'Lavado Completo',
    'services.full.desc': 'Lavado a mano por nuestro equipo, luego por el tunel automatizado.',
    'services.self.title': 'Bahias de Autoservicio',
    'services.self.desc': 'Hazlo tu mismo con equipo profesional a tu ritmo.',
    'services.self.feat1': 'Pistola de alta presion',
    'services.self.feat2': 'Cepillo de espuma',
    'services.self.feat3': 'Aspiradoras ($0.25)',
    'services.cta': 'Ver Todos los Servicios',

    // Membership
    'membership.badge': 'Lavados Ilimitados',
    'membership.title': 'Lavados Ilimitados con EverWash',
    'membership.subtitle': 'Hazte miembro y lava tu auto cuantas veces quieras por un precio mensual bajo. Sin contratos, cancela cuando quieras.',
    'membership.feat1.title': 'Lavados Ilimitados',
    'membership.feat1.desc': 'Lava tu auto cuantas veces necesites, todos los meses.',
    'membership.feat2.title': 'Sin Contratos',
    'membership.feat2.desc': 'Membresia mes a mes. Cancela cuando quieras, sin preguntas.',
    'membership.feat3.title': 'Calidad Premium',
    'membership.feat3.desc': 'Equipo Sonny con Ryko SoftGloss para una limpieza perfecta cada vez.',
    'membership.starting': 'Desde solo',
    'membership.permonth': '/mes',
    'membership.desc': 'Eso es menos que dos lavados individuales. Unete a miles de miembros felices que ahorran dinero.',
    'membership.cta': 'Comienza Tu Membresia',

    // Math
    'math.badge': 'Las Cuentas',
    'math.title': 'Simplemente Tiene Sentido',
    'math.single': 'Lavado Individual',
    'math.pervisit': 'Por visita',
    'math.best': 'Mejor Valor',
    'math.everwash': 'Membresia EverWash',
    'math.unlimited': 'Lavados ilimitados/mes',
    'math.save': 'Tu Ahorras',
    'math.saveamt': 'Por mes (4+ lavados)',
    'math.desc1': 'Lava solo',
    'math.desc2': 'dos veces al mes',
    'math.desc3': 'y ya estas ahorrando. La mayoria lava',
    'math.desc4': '4-6 veces al mes',
    'math.desc5': '- eso es mas de',
    'math.desc6': '$40 en ahorros',
    'math.desc7': 'cada mes.',
    'math.cta': 'Empieza a Ahorrar Hoy',

    // About preview
    'about.badge': 'Desde el Primer Dia',
    'about.title': 'El Original. El Mejor.',
    'about.desc': 'Super Spray Car Wash II ha mantenido los vehiculos de Tahlequah impecables desde el primer dia. Somos el tunel de lavado original y la unica opcion de servicio completo.',
    'about.cta': 'Conoce Nuestra Historia',

    // Why Choose Us
    'why.badge': 'Por Que Nosotros',
    'why.title': 'Por Que Elegir Super Spray',

    // Reviews
    'reviews.badge': 'Resenas Reales',
    'reviews.title': 'Lo Que Dicen Nuestros Clientes',
    'reviews.google': '5.0 en Google Reviews',
    'reviews.cta': 'Dejanos una Resena en Google',

    // FAQ
    'faq.badge': 'Preguntas Frecuentes',
    'faq.title': 'Preguntas Frecuentes',
    'faq.q1': 'Como funciona el lavado de servicio completo?',
    'faq.a1': 'Nuestro lavado de servicio completo es un proceso de dos pasos. Primero, nuestro equipo lava a mano y prepara tu vehiculo. Luego, lo enviamos por nuestro tunel automatizado para un acabado perfecto.',
    'faq.q2': 'Que es EverWash y como funciona?',
    'faq.a2': 'EverWash es nuestro programa de membresia de lavados ilimitados. Por un precio mensual bajo desde $20, puedes lavar tu auto cuantas veces quieras. Solo descarga la app de EverWash, registrate y listo. Sin contratos.',
    'faq.q3': 'Cual es su horario y donde estan ubicados?',
    'faq.a3': 'Estamos abiertos todos los dias de 9 AM a 7 PM. Nos encontraras en 1581 S Muskogee Ave, Tahlequah, OK 74464.',
    'faq.q4': 'Ofrecen opciones de autoservicio?',
    'faq.a4': 'Si! Ademas de nuestro lavado de servicio completo, tenemos bahias de autoservicio con equipo profesional. Tambien tenemos aspiradoras de cuarto de dolar ($0.25) con gran succion.',
    'faq.q5': 'Que tipo de equipo usan?',
    'faq.a5': 'Usamos sistemas de lavado Sonny, lideres en la industria, con tecnologia Ryko SoftGloss y FoamBrite para una limpieza segura y completa.',
    'faq.q6': 'Cuanto puedo ahorrar con una membresia?',
    'faq.a6': 'Un lavado individual cuesta $10-16. Con la membresia EverWash desde $20/mes, ahorras despues de solo dos visitas. La mayoria ahorra mas de $40 por mes.',

    // Location
    'location.badge': 'Visitanos',
    'location.title': 'Ven a Vernos',
    'location.hours': 'Horario',
    'location.hoursval': 'Abierto Diario: 9 AM - 7 PM',
    'location.address': 'Direccion',
    'location.phone': 'Telefono',
    'location.directions': 'Como Llegar',

    // Footer
    'footer.quicklinks': 'Enlaces Rapidos',
    'footer.contact': 'Contacto',
    'footer.hours': 'Horario',
    'footer.rights': 'Todos los derechos reservados.',
    'footer.serving': 'Sirviendo Tahlequah, Fort Gibson, Muskogee, Wagoner, Park Hill, Keys, Hubert y alrededores',

    // About Page
    'aboutpage.badge': 'Nuestra Historia',
    'aboutpage.title': 'El Tunel de Lavado Original de Tahlequah',
    'aboutpage.subtitle': 'El unico lavado de servicio completo en la ciudad.',
    'aboutpage.story.badge': 'Como Empezo',
    'aboutpage.story.title': 'Construido con Calidad',
    'aboutpage.story.p1': 'Super Spray Car Wash II no empezo como otro lavado de autos mas. Empezo con una creencia simple: Tahlequah merecia algo mejor.',
    'aboutpage.story.p2': 'Introdujimos algo nuevo - una experiencia de servicio completo donde cada vehiculo recibe lavado a mano antes de pasar por el tunel automatizado.',
    'aboutpage.values.badge': 'Lo Que Nos Impulsa',
    'aboutpage.values.title': 'Nuestros Valores',
    'aboutpage.community.badge': 'Sirviendo a Nuestra Comunidad',
    'aboutpage.community.title': 'Orgullosos de Servir a Tahlequah',
    'aboutpage.community.desc': 'Somos mas que un lavado de autos - somos parte de la comunidad de Tahlequah.',
    'aboutpage.community.area': 'Sirviendo el Area',
    'aboutpage.community.areadesc': 'Aunque estamos en Tahlequah, servimos a clientes de toda la region - Fort Gibson, Muskogee, Wagoner, Park Hill, Keys, Hubert y mas.',
    'aboutpage.community.welcome': 'Ya seas local o estes de paso, recibiras el mismo gran servicio.',
    'aboutpage.cta.title': 'Experimenta la Diferencia',
    'aboutpage.cta.desc': 'Ven a ver por que somos el lavado favorito de Tahlequah.',
    'aboutpage.cta.btn': 'Obten Tu Primer Lavado',

    // Memberships Page
    'membpage.badge': 'Lavados Ilimitados',
    'membpage.title': 'Membresias EverWash',
    'membpage.subtitle': 'Lava tu auto cuantas veces quieras por un precio mensual bajo. Desde solo $20/mes.',
    'membpage.how.badge': 'Simple y Facil',
    'membpage.how.title': 'Como Funciona EverWash',
    'membpage.step1': 'Descarga y Registrate',
    'membpage.step1d': 'Descarga la app de EverWash y elige tu plan. El registro toma solo minutos.',
    'membpage.step2': 'Llega y Escanea',
    'membpage.step2d': 'Cuando llegues, abre la app y escanea en la estacion de pago.',
    'membpage.step3': 'Lava Ilimitado',
    'membpage.step3d': 'Pasa cuantas veces quieras. Sin limite diario.',
    'membpage.plans.badge': 'Planes de Membresia',
    'membpage.plans.title': 'Elige Tu Plan',
    'membpage.plans.sub': 'Todos los planes incluyen lavados ilimitados sin contratos. Cancela cuando quieras.',
    'membpage.popular': 'Mas Popular',
    'membpage.single': 'Lavado individual:',
    'membpage.getstarted': 'Comenzar',
    'membpage.note': '* Los precios pueden variar. Ve los precios actuales en la app de EverWash.',
    'membpage.math.badge': 'Las Cuentas',
    'membpage.math.title': 'Simplemente Tiene Sentido',

    // Services Page
    'servpage.badge': 'Lo Que Ofrecemos',
    'servpage.title': 'Nuestros Servicios',
    'servpage.subtitle': 'Desde lavado completo hasta autoservicio, tenemos todo lo que necesitas.',
    'servpage.cta': 'Listo para un Auto Limpio?',
    'servpage.ctadesc': 'Ven cuando quieras durante el horario. Sin cita!',
    'servpage.directions': 'Como Llegar',
    'servpage.membership': 'Ver Membresias',

    // Contact Page
    'contactpage.badge': 'Contactanos',
    'contactpage.title': 'Contacto',
    'contactpage.subtitle': 'Tienes una pregunta? Nos encantaria saber de ti. Envianos un mensaje.',
    'contactpage.form.name': 'Tu Nombre',
    'contactpage.form.email': 'Correo Electronico',
    'contactpage.form.phone': 'Telefono (Opcional)',
    'contactpage.form.message': 'Tu Mensaje',
    'contactpage.form.send': 'Enviar Mensaje',
    'contactpage.form.sending': 'Enviando...',
    'contactpage.success.title': 'Mensaje Enviado!',
    'contactpage.success.desc': 'Gracias por contactarnos. Te responderemos lo antes posible.',
    'contactpage.success.call': 'Necesitas ayuda inmediata? Llamanos al',
    'contactpage.info.badge': 'Informacion',
    'contactpage.info.title': 'Visitanos',
    'contactpage.areas.badge': 'Area de Servicio',
    'contactpage.areas.title': 'Orgullosamente Sirviendo',

    // Resources Page
    'respage.badge': 'Aprende y Explora',
    'respage.title': 'Recursos',
    'respage.subtitle': 'Consejos, guias y noticias sobre el cuidado de tu auto.',
    'respage.search': 'Buscar recursos...',
    'respage.noresults': 'No se encontraron recursos.',
    'respage.readmore': 'Leer Mas',
  }
};

export default LanguageProvider;
